import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  faPlus,
  faRefresh,
} from '@fortawesome/free-solid-svg-icons';
import { AdminGuard } from '../components/RoleGuards';
import TaxesTable from '../components/taxes/TaxesTable';
import TaxForm from '../components/taxes/TaxForm';
import ActionToolbar from '../components/common/ActionToolbar';
import useTaxes from '../hooks/useTaxes';
import { useTranslation } from 'react-i18next';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { useErrorDialog } from '../context/errorDialogContext';

/**
 * TaxesPage
 * - Page for managing tax configurations (list, create, edit, delete, bulk-delete).
 * - Uses `useTaxes` hook for CRUD operations and shows a table and a modal form.
 */
const TaxesPage = () => {
  const { t } = useTranslation();
  const {
    taxes,
    loading,
    error,
    totalPages,
    totalCount,
    fetchTaxes,
    createTax,
    updateTax,
    deleteTax,
    bulkDeleteTaxes,
    clearError,
  } = useTaxes();

  const PAGE_LIMIT = 20;
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');

  const { showError } = useErrorDialog();

  /**
   * Global error watcher
   * - Shows the global error dialog when the hook reports an error
   * - Clears the error via hook if a clearError function is provided
   */
  React.useEffect(() => {
    if (error) {
      showError(error, t('taxes.page.alerts.errorTitle'));
      if (typeof clearError === 'function') clearError();
    }
  }, [error, showError, clearError]);

  // Local UI state

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingTax, setEditingTax] = useState(null);
  const [formLoading, setFormLoading] = useState(false);

  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const displayedTaxes = useMemo(() => {
    if (!Array.isArray(taxes)) return [];
    if (!searchQuery) return taxes;
    const q = searchQuery.toLowerCase();
    return taxes.filter(tax =>
      [tax.gst, tax.cgst, tax.sgst, tax.igst]
        .filter(v => v != null)
        .some(v => String(v).toLowerCase().includes(q))
    );
  }, [taxes, searchQuery]);

  const loadTaxes = useCallback((isRefresh = false) => {
    fetchTaxes({ skip: (currentPage - 1) * PAGE_LIMIT, limit: PAGE_LIMIT }, { force: isRefresh });
  }, [fetchTaxes, currentPage]);

  useEffect(() => {
    loadTaxes();
  }, [loadTaxes]);

  // Handlers
  const handleRefresh = useCallback(() => {
    loadTaxes(true);
  }, [loadTaxes]);

  const handleSearch = useCallback((searchTerm) => {
    setSearchQuery(searchTerm);
  }, []);

  /** Open form in create mode */
  const handleAddTax = useCallback(() => {
    setEditingTax(null);
    setShowForm(true);
  }, []);

  /** Open form prefilled for editing the provided tax */
  const handleEditTax = useCallback((tax) => {
    setEditingTax(tax);
    setShowForm(true);
  }, []);

  /** Prepare and open the delete confirmation for a tax (single or bulk) */
  const handleDeleteTax = useCallback((tax) => {
    setDeleteTarget(tax);
    setShowDeleteConfirm(true);
  }, []);

  /** Handle form submit for create or update operations */
  const handleFormSubmit = useCallback(async (taxData) => {
    setFormLoading(true);

    try {
      let result;
      if (editingTax) {
        result = await updateTax(editingTax.id, taxData);
      } else {
        result = await createTax(taxData);
      }

      if (result.success) {
        setShowForm(false);
        setEditingTax(null);
      }
    } finally {
      setFormLoading(false);
    }
  }, [editingTax, createTax, updateTax]);

  /** Execute the confirmed delete (single or bulk) operation */
  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;

    setDeleteLoading(true);

    try {
      let result;
      if (Array.isArray(deleteTarget)) {
        // Bulk delete
        result = await bulkDeleteTaxes(deleteTarget);
      } else {
        // eslint-disable-next-line
        result = await deleteTax(deleteTarget.id);
      }

      // Always close dialog after operation completes (success or error)
      // The error will be shown via the global error dialog or message alert
    } finally {
      setDeleteLoading(false);
      setShowDeleteConfirm(false);
      setDeleteTarget(null);
    }
  }, [deleteTarget, deleteTax, bulkDeleteTaxes]);

  /** Filters passed to the toolbar (currently empty, reserved for future use) */
  const toolbarFilters = useMemo(() => [], []);

  /**
   * Toolbar actions to be rendered in the ActionToolbar component
   * - refresh: reload taxes
   * - add-tax: open the create tax form
   */
  const toolbarActions = useMemo(() => {
    const actions = [
      {
        key: 'refresh',
        icon: faRefresh,
        onClick: handleRefresh,
        disabled: loading,
        variant: 'secondary',
        tooltip: t('common.buttons.refresh'),
        spinner: loading,
        ariaLabel: t('common.buttons.refresh'),
      },
    ];

    // Add tax button
    actions.push({
      key: 'add-tax',
      icon: faPlus,
      label: t('taxes.actions.buttons.add'),
      onClick: handleAddTax,
      disabled: loading,
      variant: 'primary',
      showLabelBreakpoint: 'sm',
    });

    return actions;
  }, [
    loading,
    handleRefresh,
    handleAddTax,
  ]);

  return (
    <AdminGuard
      fallback={
        <div className="p-6">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {t('taxes.page.accessDenied.title')}
            </h2>
            <p className="text-gray-600">{t('taxes.page.accessDenied.message')}</p>
          </div>
        </div>
      }
    >
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">
            {t('taxes.page.header.title')}
          </h1>
          <p className="text-gray-600 mt-1">
            {t('taxes.page.header.subtitle')}
          </p>
        </div>

        {/* Action Toolbar */}
        <ActionToolbar
          searchValue={searchQuery}
          onSearchChange={handleSearch}
          searchPlaceholder={t('taxes.actions.searchPlaceholder')}
          filters={toolbarFilters}
          actions={toolbarActions}
          className="mb-6"
        />

        {/* Table */}
        <div className="bg-white rounded-lg border">
          <TaxesTable
            taxes={displayedTaxes}
            loading={loading}
            onEditTax={handleEditTax}
            onDeleteTax={handleDeleteTax}
          />
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div
            className="mt-4 flex items-center justify-between rounded-xl border px-6 py-3"
            style={{ borderColor: 'rgba(0,0,0,0.08)', backgroundColor: '#fff' }}
          >
            <span className="text-sm" style={{ color: '#6b7280' }}>
              Page {currentPage} of {totalPages} — {totalCount} total
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage <= 1 || loading}
                className="rounded-lg border px-4 py-2 text-sm font-semibold transition-all duration-200 hover:scale-105 disabled:cursor-not-allowed disabled:opacity-50"
                style={{ borderColor: 'rgba(0,0,0,0.15)', color: '#6b7280' }}
              >
                Previous
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                .reduce((acc, p, idx, arr) => {
                  if (idx > 0 && p - arr[idx - 1] > 1) acc.push('ellipsis-' + p);
                  acc.push(p);
                  return acc;
                }, [])
                .map((item) =>
                  typeof item === 'string' ? (
                    <span key={item} className="px-2 text-sm" style={{ color: '#9ca3af' }}>...</span>
                  ) : (
                    <button
                      key={item}
                      type="button"
                      onClick={() => setCurrentPage(item)}
                      disabled={loading}
                      className="rounded-lg px-3 py-2 text-sm font-semibold transition-all duration-200 hover:scale-105 disabled:cursor-not-allowed"
                      style={{
                        backgroundColor: item === currentPage ? '#0066cc' : 'transparent',
                        color: item === currentPage ? '#fff' : '#374151',
                      }}
                    >
                      {item}
                    </button>
                  )
                )}
              <button
                type="button"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages || loading}
                className="rounded-lg px-4 py-2 text-sm font-semibold transition-all duration-200 hover:scale-105 disabled:cursor-not-allowed disabled:opacity-50"
                style={{ backgroundColor: '#0066cc', color: '#fff' }}
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Tax Form Modal */}
        <TaxForm
          isOpen={showForm}
          onClose={() => {
            setShowForm(false);
            setEditingTax(null);
          }}
          onSubmit={handleFormSubmit}
          tax={editingTax}
          loading={formLoading}
        />

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          isOpen={showDeleteConfirm}
          title={Array.isArray(deleteTarget) ? t('taxes.confirm.deleteBulk.title') : t('taxes.confirm.deleteSingle.title')}
          message={Array.isArray(deleteTarget)
            ? (deleteTarget.length === 1
              ? t('taxes.confirm.deleteBulk.messageSingle')
              : t('taxes.confirm.deleteBulk.messageMultiple', { count: deleteTarget.length }))
            : t('taxes.confirm.deleteSingle.message')}
          confirmLabel={Array.isArray(deleteTarget) ? t('taxes.confirm.deleteBulk.confirmLabel') : t('taxes.confirm.deleteSingle.confirmLabel')}
          loadingLabel={Array.isArray(deleteTarget) ? t('taxes.confirm.deleteBulk.loadingLabel') : t('taxes.confirm.deleteSingle.loadingLabel')}
          onConfirm={handleConfirmDelete}
          onCancel={() => {
            setShowDeleteConfirm(false);
            setDeleteTarget(null);
          }}
          confirmVariant="danger"
          loading={deleteLoading}
        />
      </div>
    </AdminGuard>
  );
};

export default TaxesPage;